import classes from 'classnames';
import OTP from 'components/OTP/OTP';
import useClientEmailQuery from 'hooks/webapi/client/useClientEmailQuery';
import useClientPhoneQuery from 'hooks/webapi/client/useClientPhoneQuery';
import { usePhoneCode } from 'pages/PhoneCode/usePhoneCode';
import { memo } from 'react';
import { useNavigate } from 'react-router';
import Card from 'components/Card/Card';
import Button from 'components/Button/Button';
import { Timeout } from 'components';
import styles from './PhoneCode.module.scss';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from 'utils/phone';

type Props = {
  showCancelFlow?: boolean;
};

export default memo(function PhoneCode({ showCancelFlow = false }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: clientPhone } = useClientPhoneQuery(true);
  const { data: clientEmail } = useClientEmailQuery(true);
  const {
    handleSubmit,
    pending,
    code,
    isCodeComplete,
    handleCodeChange,
    handleResend,
    isTimerVisible,
    handleOnTimeout,
    initialLoading,
    isWrongCode,
    isApplicationPolling,
  } = usePhoneCode();

  return (
    <>
      <Card className={styles.page} dataTest="phoneCode-layout">
        <h1 className={styles.title} data-testid="phoneCode-title">
          {t('phoneCode:title')}
        </h1>

        <p
          className={styles.subtitle}
          data-testid="phoneCode-subtitle"
          dangerouslySetInnerHTML={{
            __html: t('phoneCode:desc', {
              email: clientEmail?.email,
              phone: formatPhoneNumber(clientPhone?.mobilePhone),
            }),
          }}
        />

        <Card className={styles['otp-card']} dataTest="phoneCode-otp-card">
          <p className={styles['otp-label']} data-testid="phoneCode-otp-label">
            {t('phoneCode:otpLabel')}
          </p>

          <OTP
            dataTest="phoneCode-multifield"
            name="digitalCode"
            value={code.join('')}
            onChange={handleCodeChange}
            disabled={isApplicationPolling}
            isError={isWrongCode}
          />

          {isWrongCode && (
            <div
              className={styles['error-code']}
              data-testid="phoneCode-error-code"
            >
              {t('error:validation.incorrect_code')}
            </div>
          )}

          <div
            className={classes(
              styles['resend-wrapper'],
              isWrongCode && styles['error-margin'],
            )}
          >
            {isTimerVisible ? (
              <span className={styles.counter} data-testid="phoneCode-counter">
                <Timeout onTimeout={handleOnTimeout} />
                <span
                  className={styles['disabled-resend']}
                  data-testid="phoneCode-counter-resend"
                >
                  {t('phoneCode:resendBtn')}
                </span>
              </span>
            ) : (
              <p
                className={styles['resend-row']}
                data-testid="phoneCode-resend-row"
              >
                {t('phoneCode:resendText')}{' '}
                <button
                  disabled={initialLoading}
                  type="button"
                  className={styles['resend-btn']}
                  onClick={handleResend}
                  data-testid="phoneCode-resend-btn"
                >
                  {t('phoneCode:resendBtn')}
                </button>
              </p>
            )}
          </div>

          <Button
            disabled={initialLoading || !isCodeComplete || isWrongCode}
            onClick={handleSubmit}
            dataTest="phoneCode-submitBtn"
            pending={pending || isApplicationPolling}
          >
            {t('phoneCode:submitBtn')}
          </Button>
        </Card>

        <p
          className={styles['footer-text']}
          data-testid="phoneCode-footer-text"
          dangerouslySetInnerHTML={{
            __html: t('phoneCode:footerText'),
          }}
        />
      </Card>

      {showCancelFlow && (
        <p className={styles['cancel-row']} data-testid="phoneCode-cancel-row">
          {t('phoneCode:cancelText')}{' '}
          <button
            type="button"
            className={styles['cancel-btn']}
            onClick={() => navigate(-1)}
            data-testid="phoneCode-cancel-btn"
          >
            {t('phoneCode:cancelBtn')}
          </button>
        </p>
      )}
    </>
  );
});
